import { useAuthSelector } from "@risalabs_frontend_org/auth-wrapper";
import {
  HorizontalNavigationTab,
  toast,
} from "@risalabs_frontend_org/oasis-ui-kit";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { openModal } from "risa-oasis-ui_v2";
import CustomTable from "../../../../components/custom-table/custom-table";
import AgenticAuthOnFileAddPayerModal from "../../../../components/modals/AgenticAuthOnFileAddPayerModal/AgenticAuthOnFileAddPayerModal";
import AgenticAuthOnFileToggleDialog, {
  type AgenticAuthOnFileToggleField,
} from "../../../../components/modals/AgenticAuthOnFileToggleDialog/AgenticAuthOnFileToggleDialog";
import { ToastMessages } from "../../../../components/toasts/toastMessages";
import {
  closeModal,
  setOpenedModalId,
} from "../../../../redux/slice/modalSlice";
import { AppDispatch, RootState } from "../../../../redux/store/store";
import { AgenticAuthOnFileConfigService } from "../../../../../shared/api/services/agenticAuthOnFileConfigService";
import { AGENTIC_AUTH_ON_FILE_ORG_TABS } from "../../../../../shared/constants/agenticAuthOnFileOrgTabs";
import { ModalId } from "../../../../../shared/enums/modalId";
import { OrganizationType } from "../../../../../shared/enums/organizationTypes";
import { formatDate } from "../../../../utils/formatDate";
import { capitalizeWordsSeparatedBySpace } from "../../../../utils/stringModifications";
import SaveChangesBar from "../authorizationLetterConfiguration/components/saveChangesBar/saveChangesBar";
import "./agenticAuthOnFileConfiguration.scss";
import {
  appendAgenticAuthOnFileRemark,
  createAgenticAuthOnFileRemarkEntry,
} from "./utils/agenticAuthOnFileRemarks";
import {
  AgenticAuthOnFileRow,
  AgenticAuthOnFileTableHeaders,
  AGENTIC_AUTH_ON_FILE_UPDATED_AT_DISPLAY_FORMAT,
  generateAgenticAuthOnFileTableData,
  getAgenticAuthOnFileChangedToggleRows,
  hasAgenticAuthOnFileToggleChanges,
  parseAgenticAuthToggleRowId,
} from "./utils/agenticAuthOnFileTable";

const cloneRows = (r: AgenticAuthOnFileRow[]) =>
  structuredClone(r) as AgenticAuthOnFileRow[];

type ToggleSwitchData = {
  id: string;
  required: boolean;
  value: boolean;
} | null;

const AgenticAuthOnFileConfiguration = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { openedModalId } = useSelector((state: RootState) => state.modal);
  const [selectedOrgId, setSelectedOrgId] = useState<string>(
    OrganizationType.NYCBS,
  );
  const orgId = selectedOrgId;
  const { user } = useAuthSelector((state) => state.firebaseAuthentication);

  const [searchTerm, setSearchTerm] = useState("");
  const [rows, setRows] = useState<AgenticAuthOnFileRow[]>([]);
  const [baselineRows, setBaselineRows] = useState<AgenticAuthOnFileRow[]>([]);
  const [isFetching, setIsFetching] = useState(false);
  const [saving, setSaving] = useState(false);
  const [tableRemountKey, setTableRemountKey] = useState(0);
  const [toggleSwitchData, setToggleSwitchData] =
    useState<ToggleSwitchData>(null);

  const loadPayers = useCallback(async () => {
    if (!orgId) {
      setRows([]);
      setBaselineRows([]);
      return;
    }
    setIsFetching(true);
    try {
      const nextRows = await AgenticAuthOnFileConfigService.getPayers(orgId);
      setRows(nextRows);
      setBaselineRows(cloneRows(nextRows));
      setTableRemountKey((k) => k + 1);
    } catch {
      toast.error("Failed to load agentic auth on file payers.");
      setRows([]);
      setBaselineRows([]);
    } finally {
      setIsFetching(false);
    }
  }, [orgId]);

  useEffect(() => {
    loadPayers();
  }, [loadPayers]);

  const filteredRows = useMemo(() => {
    if (!searchTerm.trim()) return rows;
    const q = searchTerm.toLowerCase();
    return rows.filter((r) => r.payerName.toLowerCase().includes(q));
  }, [rows, searchTerm]);

  const hasUnsavedChanges = useMemo(
    () => hasAgenticAuthOnFileToggleChanges(baselineRows, rows),
    [baselineRows, rows],
  );

  const tableData = useMemo(
    () => generateAgenticAuthOnFileTableData(filteredRows),
    [filteredRows],
  );

  const updateRow = (id: string, patch: Partial<AgenticAuthOnFileRow>) => {
    setRows((prev) => prev.map((r) => (r.id === id ? { ...r, ...patch } : r)));
  };

  const handleToggleChange = (data: {
    name: string;
    required: boolean;
    value: boolean;
  }) => {
    const { rowId, field } = parseAgenticAuthToggleRowId(data.name);
    if (!field || !rowId) return;

    const mappedData = {
      id: data.name,
      required: data.required,
      value: data.value,
    };

    if (field === "live") {
      updateRow(rowId, { enabled: data.value });
    } else {
      updateRow(rowId, { agent_mode: data.value });
    }

    dispatch(
      setOpenedModalId({
        id: ModalId.AGENTIC_AUTH_ON_FILE_TOGGLE_DIALOG,
        metaData: { rowId, field, switchState: data.value },
      }),
    );
    setToggleSwitchData(mappedData);
  };

  const handleModalCancel = () => {
    if (toggleSwitchData) {
      const { rowId, field } = parseAgenticAuthToggleRowId(toggleSwitchData.id);
      if (field === "live") {
        updateRow(rowId, { enabled: !toggleSwitchData.value });
      } else if (field === "agent_mode") {
        updateRow(rowId, { agent_mode: !toggleSwitchData.value });
      }
    }
    dispatch(closeModal());
    setToggleSwitchData(null);
  };

  const handleModalConfirm = (remarks: string) => {
    if (!toggleSwitchData || !remarks.trim()) {
      dispatch(closeModal());
      setToggleSwitchData(null);
      return;
    }
    const { rowId } = parseAgenticAuthToggleRowId(toggleSwitchData.id);
    const userLabel = user?.email || user?.id || "—";
    setRows((prev) =>
      prev.map((r) => {
        if (r.id !== rowId) return r;
        const entry = createAgenticAuthOnFileRemarkEntry(
          userLabel,
          remarks.trim(),
        );
        const nextRemarks = appendAgenticAuthOnFileRemark(r.remarks, entry);
        return {
          ...r,
          remarks: nextRemarks,
          updatedAt: formatDate(
            new Date(),
            AGENTIC_AUTH_ON_FILE_UPDATED_AT_DISPLAY_FORMAT,
            "—",
          ),
        };
      }),
    );
    dispatch(closeModal());
    setToggleSwitchData(null);
  };

  useEffect(() => {
    if (openedModalId === ModalId.AGENTIC_AUTH_ON_FILE_TOGGLE_DIALOG) {
      openModal(ModalId.AGENTIC_AUTH_ON_FILE_TOGGLE_DIALOG);
    }
    if (openedModalId === ModalId.AGENTIC_AUTH_ON_FILE_ADD_PAYER_MODAL) {
      openModal(ModalId.AGENTIC_AUTH_ON_FILE_ADD_PAYER_MODAL);
    }
  }, [openedModalId]);

  const handleOpenAddPayerModal = () => {
    if (hasUnsavedChanges) {
      const ok = window.confirm(
        "You have unsaved toggle changes. Adding a payer will discard them. Continue?",
      );
      if (!ok) return;
      setRows(cloneRows(baselineRows));
      setTableRemountKey((k) => k + 1);
    }
    dispatch(
      setOpenedModalId({
        id: ModalId.AGENTIC_AUTH_ON_FILE_ADD_PAYER_MODAL,
      }),
    );
  };

  const handleCloseAddPayerModal = () => {
    dispatch(closeModal());
  };

  const handleAddPayerAdded = async () => {
    dispatch(closeModal());
    await loadPayers();
  };

  const handleDiscard = () => {
    setRows(cloneRows(baselineRows));
    setTableRemountKey((k) => k + 1);
  };

  const handleSave = async () => {
    if (!orgId || !hasUnsavedChanges) return;
    const changed = getAgenticAuthOnFileChangedToggleRows(baselineRows, rows);
    if (!changed.length) return;

    setSaving(true);
    try {
      await AgenticAuthOnFileConfigService.savePayerToggles(
        orgId,
        changed,
        user?.email || user?.id,
      );
      toast.success(
        ToastMessages.OPTION_SAVE_ALL_SUCCESS || "Changes saved successfully",
      );
      await loadPayers();
    } catch (e) {
      const message =
        e instanceof Error ? e.message : "Failed to save configuration.";
      toast.error(
        `Failed to save agentic auth on file configuration: ${message}`,
      );
    } finally {
      setSaving(false);
    }
  };

  const handleRefresh = () => {
    if (hasUnsavedChanges) {
      const ok = window.confirm(
        "You have unsaved changes. Refresh will discard them. Continue?",
      );
      if (!ok) return;
    }
    loadPayers();
  };

  const handleTabClick = (nextOrgId: string) => {
    if (nextOrgId === selectedOrgId) return;
    if (hasUnsavedChanges) {
      const ok = window.confirm(
        "You have unsaved changes. Switching organization will discard them. Continue?",
      );
      if (!ok) return;
    }
    setSelectedOrgId(nextOrgId);
  };

  const toggleModalParsed = toggleSwitchData
    ? parseAgenticAuthToggleRowId(toggleSwitchData.id)
    : null;
  const toggleModalPayerRow = toggleModalParsed
    ? rows.find((r) => r.id === toggleModalParsed.rowId)
    : undefined;

  return (
    <div className="flex h-full max-h-full flex-col overflow-hidden">
      <div className="mb-2 flex items-center gap-2 text-h11 font-bold text-primaryGray-1">
        Agentic Auth on File Config
      </div>

      <div className="agentic-auth-org-tabs border-b border-primaryGray-14">
        <HorizontalNavigationTab
          tabs={AGENTIC_AUTH_ON_FILE_ORG_TABS.map((tab) => ({
            label: tab.label,
            value: tab.id,
          }))}
          value={selectedOrgId}
          onChange={handleTabClick}
          backgroundColor="none"
        />
      </div>

      <div className="mt-2 flex min-h-0 flex-1 flex-col gap-3">
        <div className="min-h-0 flex-1">
          <CustomTable
            key={tableRemountKey}
            tableHeaders={AgenticAuthOnFileTableHeaders}
            tableData={tableData}
            tableName="agenticAuthOnFileConfiguration"
            itemsPerPage={12}
            pagesPerView={4}
            count={filteredRows.length}
            totalCount={filteredRows.length}
            startIndexOfTable={() => {}}
            endIndexOfTable={() => {}}
            searchingText={setSearchTerm}
            hideSearchBar={false}
            isFetching={isFetching}
            showExportButton={false}
            showInLineLoader={false}
            showRefreshButton
            onRefreshButton={handleRefresh}
            hidePagination={false}
            placeholder="Search by Payer Name"
            onChangeEmit={handleToggleChange}
            showOtherButton={true}
            otherButtonMeta={{
              text: "Add payer",
              onClick: handleOpenAddPayerModal,
              disabled: isFetching || !orgId,
              buttonType: "primary",
              size: "medium",
            }}
          />
        </div>

        <SaveChangesBar
          hasUnsavedChanges={hasUnsavedChanges}
          saving={saving}
          onSave={handleSave}
          onDiscard={handleDiscard}
        />
      </div>

      {openedModalId === ModalId.AGENTIC_AUTH_ON_FILE_TOGGLE_DIALOG &&
        toggleSwitchData &&
        toggleModalParsed?.field && (
          <AgenticAuthOnFileToggleDialog
            onClose={handleModalCancel}
            onConfirm={handleModalConfirm}
            metaData={{
              field: toggleModalParsed.field as AgenticAuthOnFileToggleField,
              switchState: toggleSwitchData.value,
              payerDisplayName: toggleModalPayerRow
                ? capitalizeWordsSeparatedBySpace(
                    toggleModalPayerRow.payerName.replace(/_/g, " "),
                  )
                : undefined,
            }}
          />
        )}

      {openedModalId === ModalId.AGENTIC_AUTH_ON_FILE_ADD_PAYER_MODAL &&
        orgId && (
          <AgenticAuthOnFileAddPayerModal
            organizationId={orgId}
            existingPayerKeys={rows.map((r) => r.id)}
            updatedBy={user?.email || user?.id}
            onClose={handleCloseAddPayerModal}
            onAdded={handleAddPayerAdded}
          />
        )}
    </div>
  );
};

export default AgenticAuthOnFileConfiguration;
